import styled from '@emotion/styled';
import { Button, Card, Icon } from '../';
import type { Theme } from '../../theme';
import type { MouseEvent, DragEvent } from 'react';

import type { Asset, Preset } from '../../types';
import {
  formatFileSize,
  formatDimensions,
  truncateFilename,
  getFileTypeIcon,
  isRestrictedAsset,
} from '../../utils';
import { PresetSelector } from '../PresetSelector';
import type { PresetOption } from '../PresetSelector/PresetSelector.types';

export interface AssetCardProps {
  /** Asset data */
  asset: Asset;
  /** Whether the asset is selected */
  selected?: boolean;
  /** Callback when asset is selected */
  onSelect?: (id: string) => void;
  /** Callback for download action */
  onDownload?: (id: string) => void;
  /** Callback for insert link action */
  onInsertLink?: (id: string) => void;
  /** Callback for copy action (copy URL to clipboard) */
  onCopy?: (id: string) => void;
  /** Callback for goto action (navigate to URL-type assets) */
  onGoto?: (id: string) => void;
  /** Allowed actions for this asset (overrides asset.allowedActions) */
  allowedActions?: string[];
  /** Custom className */
  className?: string;
  /** Test ID for testing (deprecated, use dataTestId) */
  'data-testid'?: string;
  /** Test identifier for automated testing */
  dataTestId?: string;
  /** Data identifier for ib-ui compatibility */
  dataId?: string;
  /** Enable drag-and-drop functionality */
  draggable?: boolean;
  /** Callback when drag starts */
  onDragStart?: (asset: Asset, event: DragEvent) => void;
  /** Callback when drag ends */
  onDragEnd?: (asset: Asset, event: DragEvent) => void;
  /** Available presets for this asset */
  presets?: Preset[];
  /** Currently selected preset ID */
  selectedPresetId?: string | null;
  /** Callback when preset selection changes */
  onPresetChange?: (presetId: string) => void;
}

const MediaContainer = styled.div`
  position: relative;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme as Theme).spacing[3]};
  right: ${({ theme }) => (theme as Theme).spacing[3]};
  background-color: ${({ theme }) => (theme as Theme).colors.highlight};
  color: ${({ theme }) => (theme as Theme).colors.white};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.full};
  padding: ${({ theme }) => (theme as Theme).spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => (theme as Theme).shadows.dropdown};
`;

const RestrictedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme as Theme).spacing[3]};
  left: ${({ theme }) => (theme as Theme).spacing[3]};
  background-color: ${({ theme }) => (theme as Theme).colors.primary};
  color: ${({ theme }) => (theme as Theme).colors.white};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.full};
  padding: ${({ theme }) => (theme as Theme).spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => (theme as Theme).shadows.dropdown};
`;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
`;

const Title = styled.h3`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.base};
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.bold};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const Description = styled.p`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: ${({ theme }) => (theme as Theme).typography.lineHeight.normal};
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  flex-wrap: wrap;
`;

const FileType = styled.span`
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.medium};
  color: ${({ theme }) => (theme as Theme).colors.highlight};
`;

const FileSize = styled.span`
  color: ${({ theme }) => (theme as Theme).colors.text.muted};
`;

const Dimensions = styled.span`
  color: ${({ theme }) => (theme as Theme).colors.text.muted};
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
  width: 100%;
  
  button {
    flex: 1;
  }
`;

const PresetWrapper = styled.div`
  padding: ${({ theme }) => (theme as Theme).spacing[3]};
  padding-top: ${({ theme }) => (theme as Theme).spacing[2]};
  border-top: 1px solid ${({ theme }) => (theme as Theme).colors.border.dotted};
`;

const CardWrapper = styled.div<{ $isDraggable: boolean }>`
  cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'pointer')};
  
  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grabbing' : 'pointer')};
  }
`;

/**
 * Convert Preset to PresetOption for PresetSelector compatibility
 */
function presetToPresetOption(preset: Preset): PresetOption {
  return {
    ...preset,
    dimensions: preset.customWidth && preset.customHeight
      ? { width: preset.customWidth, height: preset.customHeight }
      : undefined,
  };
}

/**
 * AssetCard component for displaying DAM assets
 * Uses Card UI primitive from @intelligencebank/ui-primitives
 * Based on IntelligenceBank imagecard.jsx
 */
export const AssetCard = ({
  asset,
  selected = false,
  onSelect,
  onDownload,
  onInsertLink,
  onCopy,
  onGoto,
  allowedActions,
  className = '',
  'data-testid': testId,
  dataTestId,
  dataId,
  draggable = false,
  onDragStart,
  onDragEnd,
  presets,
  selectedPresetId,
  onPresetChange,
}: AssetCardProps) => {
  const actions = allowedActions || asset.allowedActions || [];
  const isRestricted = isRestrictedAsset(asset.isPublic ?? false, actions);

  const handleCardClick = () => {
    if (onSelect && !isRestricted) {
      onSelect(asset.id);
    }
  };

  const handleDownload = (e: MouseEvent) => {
    e.stopPropagation();
    if (onDownload && !isRestricted) {
      onDownload(asset.id);
    }
  };

  const handleInsertLink = (e: MouseEvent) => {
    e.stopPropagation();
    if (onInsertLink && !isRestricted) {
      onInsertLink(asset.id);
    }
  };

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation();
    if (onCopy && !isRestricted) {
      onCopy(asset.id);
    }
  };

  const handleGoto = (e: MouseEvent) => {
    e.stopPropagation();
    if (onGoto && !isRestricted) {
      onGoto(asset.id);
    }
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    if (!draggable || isRestricted) {
      e.preventDefault();
      return;
    }
    
    // Set drag data with asset information
    e.dataTransfer.setData('application/json', JSON.stringify({
      assetId: asset.id,
      assetName: asset.name,
      assetType: asset.fileType,
      thumbnail: asset.thumbnail,
    }));
    e.dataTransfer.setData('text/plain', asset.name);
    e.dataTransfer.effectAllowed = 'copy';
    
    if (onDragStart) {
      onDragStart(asset, e);
    }
  };

  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    if (onDragEnd) {
      onDragEnd(asset, e);
    }
  };

  const handlePresetChange = (presetId: string) => {
    if (onPresetChange) {
      onPresetChange(presetId);
    }
  };

  const showDownload = onDownload && actions.includes('resource_download');
  const showInsertLink = onInsertLink && actions.includes('resource_link') && !isRestricted;
  const showCopy = onCopy && actions.includes('copy');
  const showGoto = onGoto && actions.includes('goto');
  const showPresets = presets && presets.length > 0 && onPresetChange;
  const hasActions = showDownload || showInsertLink || showCopy || showGoto;

  // Convert presets to PresetOption format
  const presetOptions: PresetOption[] = presets
    ? presets.map(presetToPresetOption)
    : [];

  return (
    <CardWrapper
      $isDraggable={draggable && !isRestricted}
      draggable={draggable && !isRestricted}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Card
        variant="elevated"
        interactive
        selected={selected}
        disabled={isRestricted}
        onClick={handleCardClick}
        className={className}
        aria-label={`Asset: ${asset.name}`}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
      <MediaContainer>
        <Card.Media src={asset.thumbnail} alt={asset.name} aspectRatio="16:9" />
        {selected && (
          <SelectedBadge>
            <Icon name="check_circle" size="md" />
          </SelectedBadge>
        )}
        {isRestricted && (
          <RestrictedBadge>
            <Icon name="lock" size="sm" />
          </RestrictedBadge>
        )}
      </MediaContainer>

      <Card.Content>
        <ContentInner>
          <Header>
            <Icon name={getFileTypeIcon(asset.filename || asset.name)} size="sm" />
            <Title title={asset.name}>{truncateFilename(asset.name, 30)}</Title>
          </Header>

          {asset.description && <Description>{asset.description}</Description>}

          <Metadata>
            <FileType>{asset.fileType.toUpperCase()}</FileType>
            <FileSize>{formatFileSize(asset.fileSize)}</FileSize>
            {asset.width && asset.height && (
              <Dimensions>{formatDimensions(asset.width, asset.height)}</Dimensions>
            )}
          </Metadata>
        </ContentInner>
      </Card.Content>

      {hasActions && (
        <Card.Actions align="space-between">
          <ActionsWrapper>
            {showDownload && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={isRestricted}
                aria-label="Download asset"
                data-testid={testId ? `${testId}-download` : undefined}
              >
                <Icon name="download" size="sm" />
                Download
              </Button>
            )}
            {showCopy && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopy}
                disabled={isRestricted}
                aria-label="Copy asset URL"
                data-testid={testId ? `${testId}-copy` : undefined}
              >
                <Icon name="content_copy" size="sm" />
                Copy
              </Button>
            )}
            {showGoto && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleGoto}
                disabled={isRestricted}
                aria-label="Go to asset"
                data-testid={testId ? `${testId}-goto` : undefined}
              >
                <Icon name="launch" size="sm" />
                Go to
              </Button>
            )}
            {showInsertLink && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleInsertLink}
                disabled={isRestricted}
                aria-label="Insert link"
                data-testid={testId ? `${testId}-insert-link` : undefined}
              >
                <Icon name="link" size="sm" />
                Insert Link
              </Button>
            )}
          </ActionsWrapper>
        </Card.Actions>
      )}

        {showPresets && (
          <PresetWrapper onClick={(e) => e.stopPropagation()}>
            <PresetSelector
              presets={presetOptions}
              value={selectedPresetId ?? null}
              onChange={handlePresetChange}
              size="sm"
              fullWidth
              showDetails
              placeholder="Select preset..."
              data-testid={testId ? `${testId}-preset-selector` : undefined}
            />
          </PresetWrapper>
        )}
      </Card>
    </CardWrapper>
  );
};

AssetCard.displayName = 'AssetCard';